import { useState } from 'react'
import { useFormik } from "formik";
import * as Yup from "yup";
import Header from '../../Header/Header.js';
import SideBar from '../SideBar.js';
import { useMutation } from '@apollo/client';
import { CREATE_SUPPORT } from '../../graphql/Mutation/Support.js';
import SpinLoader from '../../components/SpinLoader.js';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { IMAGE_URL } from '../../config.js';
import { useFileUpload } from '../../Common/PreSignUrl.jsx';

const TechRelated = () => {
    const { handleFileUpload } = useFileUpload();
    const location = useLocation();
    const selectedIssue = location.state?.selectedIssue || ""; // Get issue title from state
    const [handleinputsupport, { loading }] = useMutation(CREATE_SUPPORT);
    const [shipmentAttachment, setShipmentAttachment] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            subject: "",
            issue: "",
            attachment: null,
        },
        validationSchema: Yup.object({
            subject: Yup.string().required("Subject is required"),
            issue: Yup.string()
                .max(300, "Issue must be within 300 characters")
                .required("Issue description is required"),
        }),
        onSubmit: async (values, { resetForm }) => {
            try {
                const response = await handleinputsupport({
                    variables: {
                        input: {
                            ticket_type_issue: selectedIssue === "Billing & Remittance" ? "Billing" :
                                selectedIssue === "Weight Related Issues" ? "Weight" :
                                    selectedIssue === "Tech Related Issues" ? "Tech" :
                                        "Other",
                            issue_category: values?.subject,
                            issue_type: selectedIssue,
                            message: {  // Pass the message here
                                sender: "seller",  // You can modify this dynamically depending on the user type
                                message: values?.issue,
                                attachments: shipmentAttachment ? [shipmentAttachment] : [],
                            },
                        },
                    },
                });

                // console.log('✌️response --->', response);
                if (response?.data?.createSupport?.status) {
                    toast.success(response.data.createSupport.message);
                    resetForm();
                    setShipmentAttachment("");
                } else {
                    toast.error(response.data.createSupport.message);
                }
            } catch (err) {
                toast.error(err);
            }
        },
    });

    const handleFileChange = (event, keyPrefix, folderName) => {
        if ((!event?.target?.files || !event?.currentTarget?.files) || event?.target?.files?.length === 0) {
            setIsLoading(false);
            // console.log("===");
        }
        handleFileUpload({
            event,
            keyPrefix,
            setStateCallback: (url) => {
                const newUrl = url.split('/').slice(-2).join('/');
                // console.log('✌️newUrl --->', newUrl);
                setShipmentAttachment(newUrl)
                setIsLoading(false);
            },
            setIsLoading,
            folderName
        });
    }

    return (
        <>
            <Header />

            <div className="flex min-h-screen bg-slate-100 overflow-hidden">
                {/* Sidebar */}

                <SideBar />

                {/* Main Content */}
                <div className="flex-1 space-y-3 px-0 py-6 md:p-6 overflow-x-hidden ml-16">
                    <div className="max-w-3xl justify-start bg-white p-6 rounded-lg shadow-lg">
                        {/* <div className="max-w-lg w-full bg-white p-6 rounded-xl shadow-md"> */}
                        <h2 className="text-2xl font-bold text-gray-800 mb-3">{selectedIssue}</h2>
                        <p className="text-sm text-gray-500 mb-6">Fill the form below to raise a request.</p>

                        <form onSubmit={formik.handleSubmit} className="space-y-4 md:p-6 md:bg-slate-100">
                            {/* Subject */}
                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-700">Subject</label>
                                <input
                                    type="text"
                                    name="subject"
                                    className="w-full mt-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none"
                                    placeholder="Enter subject"
                                    {...formik.getFieldProps("subject")}
                                />
                                {formik.touched.subject && formik.errors.subject && (
                                    <p className="text-red-500 text-sm mt-1">{formik.errors.subject}</p>
                                )}
                            </div>

                            {/* Issue Description */}
                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-700">Add your issue</label>
                                <textarea
                                    name="issue"
                                    className="w-full mt-1 px-3 py-1.5 border border-gray-300 rounded-lg h-24 focus:outline-none resize-none"
                                    placeholder="Describe your issue"
                                    maxLength={300}
                                    {...formik.getFieldProps("issue")}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    {formik.values.issue.length} / 300 Characters Remaining
                                </p>
                                {formik.touched.issue && formik.errors.issue && (
                                    <p className="text-red-500 text-sm mt-1">{formik.errors.issue}</p>
                                )}
                            </div>

                            <p className="text-sm mt-2 text-gray-500">
                                Please mention AWB Numbers for better response (if available)
                            </p>

                            {/* File Upload */}
                            <div>
                                <label className="block mb-2 text-sm font-medium text-gray-700">Attachments (If any)</label>
                                <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-slate-50 hover:bg-slate-100 transition">
                                    <input
                                        type="file"
                                        className="hidden"
                                        id="file-upload"
                                        accept="image/*"
                                        onChange={(e) => handleFileChange(e, "techIssue", "ticket")}
                                    />
                                    <label
                                        htmlFor="file-upload"
                                        className="cursor-pointer flex flex-col items-center space-y-2"
                                    >
                                        <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V8a5 5 0 0110 0v8m-6 4h4m-4 0H9m2 0V8m0 12v2"></path>
                                        </svg>
                                        <span className="text-slate-400 font-medium">Click to upload</span>
                                        <span className="text-xs text-slate-400">JPG, PNG, GIF up to 5MB</span>
                                    </label>
                                    {formik.values.attachment && (
                                        <p className="text-sm text-gray-700 mt-2">{formik.values.attachment.name}</p>
                                    )}
                                </div>
                                {shipmentAttachment && (
                                    <div className="mt-4">
                                        <p className="text-sm font-medium text-gray-700">Image Preview:</p>
                                        <img
                                            src={`${IMAGE_URL}/${shipmentAttachment}`}
                                            alt="Preview"
                                            className="mt-2 w-full h-auto rounded-lg shadow-md"
                                        />
                                    </div>
                                )}
                            </div>


                            {/* Submit Button */}
                            <button
                                type="submit"
                                className="w-full py-2 bg-slate-800 text-white font-medium rounded-lg hover:bg-slate-900 transition"
                            >
                                Submit <span>{loading && <SpinLoader />}</span>
                            </button>
                        </form>
                        {/* </div> */}
                    </div>

                </div>
            </div>
        </>
    )
}

export default TechRelated
